import { QueryLazyOptions, useMutation, useQuery } from "@apollo/client";
import { Formik, FormikProps } from "formik";
import gql from "graphql-tag";
import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { useCallback, useMemo } from "react";
import useMultiBrands from "src/cache-hooks/useIsMultiBrand";
import { PhoenixInputField } from "src/Components/Field/Phoenix";
import { xIcon } from "src/images/NewDesign";
import { OptionItem } from "src/types";
import { useDraggable } from "src/utils/hooks";
import styled from "styled-components";
import * as Yup from "yup";
import { SHOW_LEAD_SOURCE_TO_USER } from "../../../apollo/query";
import { sortedCountryCodesData } from "../../../static/countries";
import { isValidEmail, SYSTEM_FIELD_CHAR_LIMIT, SYSTEM_FIELD_EMAIL_CHAR_LIMIT } from "../../../utils/format";
import { recursiveObjectTrim } from "../../../utils/misc";
import { theme } from "../../../utils/theme";
import { appToast, errorToast } from "../../../utils/toast";
import { PhoenixMultiSelectField } from "../../Field/Phoenix";
import { AppErrorText, AppText } from "../../UI";
import { PhoenixAppButton, PhoenixIcon, PhoenixMultiSelect } from "../../UI/Phoenix";
import { StateDropdown } from "../LeadCard/StateDropdown";
import { NewLeadFormV2Skeleton } from "./NewLeadFormV2Skeleton";
/**
 * Used on the dialer for an incoming call in which the lead is not known
 */

const CHANNEL_OPTIONS: OptionItem[] = [
  {
    label: "Inbound",
    value: "Inbound",
  },
  {
    label: "Outbound",
    value: "Outbound",
  },
];

const OPTIONS_LOADING = [
  {
    label: "Loading...",
    value: "",
  },
];

interface NewLeadFormProps {
  refetch?: (options?: QueryLazyOptions<Record<string, any>> | undefined) => void;
  lead_id: string;
  onClose: () => void;
}

export const ADD_LEAD = gql`
  mutation addLead(
    $first_name: String
    $last_name: String
    $business_name: String
    $email: String
    $industry: String
    $sub_industry: String
    $lead_source: String
    $channel: CHANNEL
    $status: String
    $title: String
    $content: String
    $city: String
    $state: String
    $country: String
    $country_code: String
    $brand_id: String
  ) {
    addLead(
      first_name: $first_name
      last_name: $last_name
      business_name: $business_name
      email: $email
      industry: $industry
      sub_industry: $sub_industry
      lead_source: $lead_source
      channel: $channel
      status: $status
      title: $title
      content: $content
      city: $city
      state: $state
      country: $country
      country_code: $country_code
      brand_id: $brand_id
    ) {
      id
      lead {
        id
        first_name
        last_name
        full_name
        business_name
        primary_email
        primary_phone_number
        industry
        sub_industry
        lead_source
        channel
        status
        title
        content
        city
        state
        timezone_by_state
      }
    }
  }
`;

const UPDATE_LEAD = gql`
  mutation updateLead(
    $id: String!
    $first_name: String
    $last_name: String
    $business_name: String
    $primary_email: String
    $industry: String
    $sub_industry: String
    $lead_source: String
    $channel: CHANNEL
    $city: String
    $state: String
    $country: String
    $country_code: String
    $brand_id: String
  ) {
    updateLead(
      id: $id
      first_name: $first_name
      last_name: $last_name
      business_name: $business_name
      primary_email: $primary_email
      industry: $industry
      sub_industry: $sub_industry
      lead_source: $lead_source
      channel: $channel
      city: $city
      state: $state
      country: $country
      country_code: $country_code
      brand_id: $brand_id
    ) {
      id
      first_name
      last_name
      full_name
      business_name
      primary_email
      primary_phone_number
      industry
      sub_industry
      lead_source
      channel
      city
      state
      country
      timezone_by_state
    }
  }
`;

interface IndustryOption {
  id: string;
  label: string;
  sub_industries: string[];
}

const GET_POSSIBLE_INDUSTRIES_DATA = gql`
  query fetchIndustryOptions {
    fetchIndustryOptions {
      id
      label
      sub_industries
    }
  }
`;

const GET_POSSIBLE_LEAD_SOURCES_DATA = gql`
  query fetchLeadSourceOptions {
    fetchLeadSourceOptions {
      id
      label
    }
  }
`;

const newLeadSchema = Yup.object().shape({
  first_name: Yup.string()
    .notRequired()
    .nullable()
    .max(SYSTEM_FIELD_CHAR_LIMIT, `Must be ${SYSTEM_FIELD_CHAR_LIMIT} characters or less`),
  last_name: Yup.string()
    .notRequired()
    .nullable()
    .max(SYSTEM_FIELD_CHAR_LIMIT, `Must be ${SYSTEM_FIELD_CHAR_LIMIT} characters or less`),
  business_name: Yup.string()
    .required("Field is required")
    .max(SYSTEM_FIELD_CHAR_LIMIT, `Must be ${SYSTEM_FIELD_CHAR_LIMIT} characters or less`),
  channel: Yup.string().required("Field is required"),
  email: Yup.string()
    .notRequired()
    .email()
    .nullable()
    .max(SYSTEM_FIELD_EMAIL_CHAR_LIMIT, `Must be ${SYSTEM_FIELD_EMAIL_CHAR_LIMIT} characters or less`)
    .test("email", "Must be a valid email", (value) => {
      if (!!value) {
        return isValidEmail(value) ?? true;
      }
      return true;
    }),
  lead_source: Yup.string().notRequired().nullable(),
  industry: Yup.string().notRequired().nullable(),
  sub_industry: Yup.string().notRequired().nullable(),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required").trim(),
  country_code: Yup.object()
    .required("Country is required")
    .shape({
      label: Yup.string(),
      value: Yup.object().shape({ iso2: Yup.string(), code: Yup.string() }),
    }),
  brand_id: Yup.string().when("orgHasMultipleBrands", {
    is: (orgHasMultipleBrands: boolean) => orgHasMultipleBrands,
    then: Yup.string().required("Brand is required"),
    otherwise: Yup.string().notRequired(),
  }),
});

interface MyFormikProps {
  first_name?: string;
  last_name?: string;
  business_name: string;
  email: string;
  lead_source: string;
  industry: string;
  sub_industry: string;
  channel: string;
  city: string;
  state: string;
  country_code: { label: string; value: { iso2: string; code: string } };
  brand_id: string | undefined;
}

const NewLeadFormV2: React.FC<NewLeadFormProps> = ({ refetch, lead_id, onClose }) => {
  const { draggableRef, handleDragStart, dragState } = useDraggable();
  const { data: showLeadSourceToUser } = useQuery(SHOW_LEAD_SOURCE_TO_USER);
  const { showMultiBrandUI, USER_ASSIGNED_BRAND_OPTIONS } = useMultiBrands(useFlags);

  const [updateLead, { loading: updateLoading, error: updateError }] = useMutation(UPDATE_LEAD, {
    async onCompleted({ updateLead }) {
      if (!updateLead) {
        appToast("Error adding lead. Something went wrong.");
        return;
      }
      appToast("Lead updated!");
      !!refetch && refetch();
      onClose?.();
    },
    onError({ message }) {
      errorToast(message);
    },
    refetchQueries: ["fetchLead", "FetchAssociateContact"],
  });

  interface LeadSourceOption {
    id: string;
    label: string;
  }

  const { data: leadsourceData, loading: leadsourceLoading, error: leadsourceError } = useQuery(
    GET_POSSIBLE_LEAD_SOURCES_DATA,
    {
      fetchPolicy: "cache-and-network",
      onError({ message, name }) {
        console.log(`Error in ${name}: `, message);
      },
    },
  );
  const { data: industriesData, loading: industriesLoading, error: industriesError } = useQuery(
    GET_POSSIBLE_INDUSTRIES_DATA,
    {
      fetchPolicy: "cache-and-network",
      onError({ message, name }) {
        console.log(`Error in ${name}: `, message);
      },
    },
  );

  const findSubIndustryOptions = useCallback(
    (industry: string) => {
      if (industriesLoading) {
        return OPTIONS_LOADING;
      }

      if (!industry || industry === "") {
        return [{ label: "Select Industry First", value: "" }];
      }

      const found = industriesData?.fetchIndustryOptions
        .slice()
        .filter((item: IndustryOption) => item.label === industry);
      if (!!found.length) {
        return found[0].sub_industries
          ?.map((ls: string) => ({ label: ls, value: ls }))
          ?.sort((a: any, b: any) => (a?.label < b?.label ? -1 : 1));
      }

      return [];
    },
    [industriesData, industriesLoading],
  );

  const LEAD_SOURCE_OPTIONS = useMemo(() => {
    return (
      leadsourceData?.fetchLeadSourceOptions?.map((ls: LeadSourceOption) => ({ label: ls.label, value: ls.label })) ||
      []
    );
  }, [leadsourceData]);

  const INDUSTRY_OPTIONS = useMemo(() => {
    return (
      industriesData?.fetchIndustryOptions?.map((ls: IndustryOption) => ({ label: ls.label, value: ls.label })) || []
    );
  }, [industriesData]);

  if (leadsourceLoading || industriesLoading || updateLoading) {
    return <NewLeadFormV2Skeleton />;
  }

  if (leadsourceError) return <AppErrorText>Error loading Lead Sources</AppErrorText>;

  if (industriesError) return <AppErrorText>Error loading Industries</AppErrorText>;

  return (
    <Formik
      enableReinitialize
      initialValues={{
        first_name: "",
        last_name: "",
        business_name: "",
        email: "",
        lead_source: "",
        channel: "",
        industry: "",
        sub_industry: "",
        city: "",
        state: "",
        country_code: { label: "United States (1)", value: { iso2: "US", code: "1" } },
        brand_id: undefined,
        orgHasMultipleBrands: !!showMultiBrandUI,
      }}
      validateOnChange
      validateOnMount
      validationSchema={newLeadSchema}
      onSubmit={async (values) => {
        if (!lead_id) {
          // lead should already exist in the database
          errorToast("Something went wrong. Please try again.");
          return;
        }

        recursiveObjectTrim(values);

        await updateLead({
          variables: {
            id: lead_id,
            first_name: values.first_name,
            last_name: values.last_name,
            business_name: values.business_name,
            primary_email: values.email,
            lead_source: values.lead_source,
            channel: values.channel,
            industry: values.industry,
            sub_industry: values.sub_industry,
            city: values.city,
            state: values.state,
            country: values.country_code?.value?.iso2,
            country_code: values.country_code?.value?.code,
            brand_id: values.brand_id,
          },
        });
      }}
    >
      {({
        submitForm,
        values,
        setFieldValue,
        isSubmitting,
        errors,
        validateForm,
        isValid,
      }: FormikProps<MyFormikProps>) => {
        console.log("errors: ", errors);

        return (
          <PopupContainerDiv ref={draggableRef}>
            <Header onMouseDown={handleDragStart} dragState={dragState}>
              <AppText fontSize={14} fontWeight={600} lineHeight={20} color={theme.WHITE_COLOR}>
                Create New Lead
              </AppText>
              <PhoenixIcon svg={xIcon} size={16} variant="white" hoverColor="white" pointer onClick={onClose} />
            </Header>

            <FormContent>
              <FormGrid>
                <InputFieldWrapper>
                  <PhoenixInputField
                    titleText="Contact First Name"
                    name="first_name"
                    placeholder="Enter first name"
                    width={210}
                  />
                </InputFieldWrapper>

                <InputFieldWrapper>
                  <PhoenixInputField
                    titleText="Contact Last Name"
                    name="last_name"
                    placeholder="Enter last name"
                    width={210}
                  />
                </InputFieldWrapper>

                <InputFieldWrapper>
                  <PhoenixInputField
                    titleText="Business Name"
                    name="business_name"
                    placeholder="Enter business name"
                    width={210}
                    requiredStar
                  />
                </InputFieldWrapper>

                <PhoenixMultiSelect
                  titleTextSpacing={4}
                  titleText="Channel"
                  isMulti={false}
                  name="channel"
                  value={CHANNEL_OPTIONS?.find((option: OptionItem) => option.value === values.channel)}
                  options={CHANNEL_OPTIONS}
                  onChange={(selected: any) => setFieldValue("channel", selected?.value || "")}
                  placeholder="Select channel"
                  width={210}
                  required
                />

                <InputFieldWrapper>
                  <PhoenixInputField titleText="Primary Email" name="email" placeholder="Enter email" width={210} />
                </InputFieldWrapper>

                {showLeadSourceToUser?.showLeadSourceToUser && (
                  <PhoenixMultiSelect
                    titleTextSpacing={4}
                    titleText="Lead Source"
                    isMulti={false}
                    name="lead_source"
                    value={LEAD_SOURCE_OPTIONS?.find((option: OptionItem) => option.value === values.lead_source)}
                    options={LEAD_SOURCE_OPTIONS}
                    onChange={(selected: any) => setFieldValue("lead_source", selected?.value || "")}
                    placeholder="Select lead source"
                    width={210}
                  />
                )}

                <PhoenixMultiSelect
                  titleTextSpacing={4}
                  titleText="Industry"
                  isMulti={false}
                  name="industry"
                  value={INDUSTRY_OPTIONS?.find((option: OptionItem) => option.value === values.industry)}
                  options={INDUSTRY_OPTIONS}
                  onChange={(selected: any) => {
                    setFieldValue("industry", selected?.value || "");
                    setFieldValue("sub_industry", "");
                  }}
                  placeholder="Select industry"
                  width={210}
                />

                <PhoenixMultiSelect
                  titleTextSpacing={4}
                  titleText="Sub-Industry"
                  isMulti={false}
                  name="sub_industry"
                  value={
                    values.sub_industry
                      ? findSubIndustryOptions(values.industry)?.find(
                          (option: any) => option.value === values.sub_industry,
                        )
                      : null
                  }
                  options={findSubIndustryOptions(values.industry)}
                  onChange={(selected: any) => setFieldValue("sub_industry", selected?.value || "")}
                  width={210}
                  isDisabled={!values.industry}
                />

                <StateDropdown
                  titleTextSpacing={4}
                  titleText="State/Province"
                  required
                  country_code={values.country_code?.value?.iso2}
                  current_value={values.state}
                  callback={setFieldValue}
                  hideErrorMessage
                />

                <InputFieldWrapper>
                  <PhoenixInputField titleText="City" name="city" placeholder="Enter city" width={210} requiredStar />
                </InputFieldWrapper>

                <PhoenixMultiSelect
                  titleTextSpacing={4}
                  titleText="Country"
                  isMulti={false}
                  name="country_code"
                  value={values.country_code}
                  options={sortedCountryCodesData}
                  onChange={(selected: any) => {
                    setFieldValue("country_code", selected);
                    setFieldValue("state", "");
                  }}
                  placeholder="Select country"
                  width={210}
                  required
                />

                {showMultiBrandUI && (
                  <PhoenixMultiSelectField
                    titleTextSpacing={4}
                    titleText="Brand"
                    isClearable={false}
                    isMulti={false}
                    name="brand_id"
                    options={USER_ASSIGNED_BRAND_OPTIONS}
                    onChange={(e: any) => {
                      setFieldValue("brand_id", e?.value);
                    }}
                    value={USER_ASSIGNED_BRAND_OPTIONS?.find((option: OptionItem) => option.value === values.brand_id)}
                    placeholder={
                      USER_ASSIGNED_BRAND_OPTIONS?.length > 0
                        ? "Select a brand"
                        : "No assigned brands. Please contact an enablement user."
                    }
                    hideErrorMessage
                    required
                  />
                )}
              </FormGrid>

              <ButtonContainer>
                <PhoenixAppButton
                  type="submit"
                  onClick={submitForm}
                  variant="brand"
                  buttonType="primary"
                  disabled={isSubmitting || !isValid}
                >
                  Update Lead
                </PhoenixAppButton>
              </ButtonContainer>
            </FormContent>
          </PopupContainerDiv>
        );
      }}
    </Formik>
  );
};

const PopupContainerDiv = styled.div`
  display: flex;
  position: fixed;
  top: 12px;
  left: 12px;
  flex-direction: column;
  background: ${theme.WHITE_COLOR};
  border-radius: 8px;
  border: 1px solid ${theme.NEUTRAL200};
  width: fit-content;
  max-width: 90vw;
  max-height: 90vh;
  overflow: hidden;
  z-index: 9000;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);
`;

const Header = styled.div<{ dragState?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: ${theme.PRIMARY900};
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  cursor: ${(props) => (props.dragState ? "grabbing" : "grab")};
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px;
  gap: 8px;
  max-height: calc(90vh - 40px);
  overflow-y: auto;
  overflow-x: hidden;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 8px;
  align-items: start;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 0px;
`;

const InputFieldWrapper = styled.div`
  margin-bottom: 2px;
`;

export { NewLeadFormV2 };
